import React, { useEffect, useState, useRef } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Send, Loader2, User } from "lucide-react";
import { toast } from "sonner";

function fmt(ts) {
  try { return new Date(ts).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [convos, setConvos] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const endRef = useRef(null);

  const loadConvos = () => { if (isAdmin) api.get("/chat/conversations").then((r) => setConvos(r.data)).catch(() => {}); };
  const loadMessages = () => {
    if (isAdmin && !activeClient) return;
    const url = isAdmin ? `/chat/messages?client_id=${activeClient.client_id}` : "/chat/messages";
    api.get(url).then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => { loadConvos(); if (!isAdmin) loadMessages(); if (isAdmin) api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin && activeClient) loadMessages(); }, [activeClient]);
  useEffect(() => {
    const t = setInterval(() => { loadConvos(); loadMessages(); }, 8000);
    return () => clearInterval(t);
  }, [activeClient]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    if (isAdmin && !activeClient) { toast.error("Selecciona una conversación"); return; }
    setSending(true);
    try {
      const payload = isAdmin ? { text, client_id: activeClient.client_id } : { text };
      await api.post("/chat/messages", payload);
      setText("");
      loadMessages(); loadConvos();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const Bubble = ({ m }) => {
    const mine = m.sender_role === (isAdmin ? "admin" : "client");
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[75%] px-3 py-2 text-sm ${mine ? "bg-corp text-white" : "bg-muted text-foreground"}`}>
          {!mine && <div className="text-[10px] font-bold uppercase opacity-70 mb-0.5">{m.sender_name}</div>}
          <div className="whitespace-pre-wrap break-words">{m.text}</div>
          <div className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{fmt(m.created_at)}</div>
        </div>
      </div>
    );
  };

  const composer = (
    <div className="flex gap-2 border-t border-border p-3">
      <input data-testid="chat-input" className="flex-1 bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-corp"
        placeholder="Escribe un mensaje..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
      <button data-testid="chat-send-btn" onClick={send} disabled={sending} className="bg-corp hover:bg-corp-hover text-white px-4 flex items-center gap-2 transition-colors disabled:opacity-60">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader title={isAdmin ? "Mensajes" : "Soporte / Chat"} subtitle={isAdmin ? "Conversaciones con clientes" : "Habla con nuestro equipo"} />
        {isAdmin ? (
          <div className="grid md:grid-cols-[300px_1fr] border border-border bg-card" style={{ height: "70vh" }}>
            <div className="border-r border-border overflow-y-auto">
              <div className="p-2 border-b border-border">
                <select data-testid="new-chat-select" className="w-full bg-background border border-input px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-corp" value=""
                  onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); if (c) setActiveClient({ client_id: c.id, client_name: c.name }); }}>
                  <option value="">➕ Nuevo chat con cliente...</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {convos.length === 0 ? <div className="p-6 text-sm text-muted-foreground">Sin conversaciones.</div> :
                convos.map((c) => (
                  <button key={c.client_id} data-testid={`convo-${c.client_id}`} onClick={() => setActiveClient(c)}
                    className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition-colors ${activeClient?.client_id === c.client_id ? "bg-freight/10" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{c.client_name}</span>
                      {c.unread > 0 && <span className="bg-freight text-black text-[10px] font-bold px-1.5 rounded-full">{c.unread}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{c.last_text}</div>
                  </button>
                ))}
            </div>
            <div className="flex flex-col min-h-0">
              {activeClient ? (
                <>
                  <div className="px-4 py-3 border-b border-border font-bold flex items-center gap-2"><User className="w-4 h-4 text-corp" />{activeClient.client_name}</div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {messages.map((m, i) => <Bubble key={i} m={m} />)}
                    <div ref={endRef} />
                  </div>
                  {composer}
                </>
              ) : <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-6 text-center">Selecciona una conversación para responder</div>}
            </div>
          </div>
        ) : (
          <div className="border border-border bg-card flex flex-col" style={{ height: "70vh" }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.length === 0 ? <div className="text-sm text-muted-foreground text-center mt-8">Envíanos tu primer mensaje. Te responderemos lo antes posible.</div> :
                messages.map((m, i) => <Bubble key={i} m={m} />)}
              <div ref={endRef} />
            </div>
            {composer}
          </div>
        )}
      </div>
    </Layout>
  );
}
